/**
 * Shared email validation utility for mailing systems
 * Used by both webinar-mailings and plx-mailings
 */

import {
  validatePlaceholders,
  PLX_PLACEHOLDERS,
  WEBINAR_PLACEHOLDERS,
  PlaceholderConfig,
} from './placeholderValidation';

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface ValidationConfig {
  /** Placeholder config for unknown/required detection (shared helper) */
  placeholders?: PlaceholderConfig;
  /** Maximum recommended subject length (default: 60) */
  maxSubjectLength?: number;
  /** Whether to check for unsubscribe link (default: true) */
  requireUnsubscribeLink?: boolean;
}

/**
 * Validate email content for mailing campaigns
 */
export function validateEmailContent(
  subject: string,
  htmlBody: string,
  config: ValidationConfig = {},
): ValidationResult {
  const { placeholders, maxSubjectLength = 60, requireUnsubscribeLink = true } = config;

  const errors: string[] = [];
  const warnings: string[] = [];

  if (subject && subject.length > maxSubjectLength) {
    warnings.push(
      `Betreff ist ${subject.length} Zeichen lang (empfohlen: max. ${maxSubjectLength})`,
    );
  }

  if (placeholders && htmlBody) {
    errors.push(...validatePlaceholders(htmlBody, subject || '', placeholders));
  }

  if (requireUnsubscribeLink && htmlBody) {
    const hasUnsubscribe =
      htmlBody.toLowerCase().includes('unsubscribe') ||
      htmlBody.toLowerCase().includes('abmelden') ||
      htmlBody.toLowerCase().includes('abbestellen') ||
      htmlBody.includes('{UNSUBSCRIBE}') ||
      htmlBody.includes('{{unsubscribeUrl}}');

    if (!hasUnsubscribe) {
      warnings.push('Kein Abmeldelink gefunden');
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Pre-configured validation for PLX mailings
 * Note: Unsubscribe link is in the ESP template, not user content
 */
export function validatePlxEmail(subject: string, htmlBody: string): ValidationResult {
  return validateEmailContent(subject, htmlBody, {
    placeholders: {
      allowed: PLX_PLACEHOLDERS,
      required: 'PRODUCT_LINK',
      system: 'plx',
    },
    maxSubjectLength: 60,
    requireUnsubscribeLink: false,
  });
}

/**
 * Pre-configured validation for webinar mailings
 */
export function validateWebinarEmail(subject: string, htmlBody: string): ValidationResult {
  return validateEmailContent(subject, htmlBody, {
    placeholders: {
      allowed: WEBINAR_PLACEHOLDERS,
      required: 'WEBINAR_LINK',
      system: 'webinar',
    },
    maxSubjectLength: 60,
    requireUnsubscribeLink: true,
  });
}
